import { useApplicationContext } from "@src/context/ApplicationContext";
import { useThemeContext } from "@src/context/ThemeContext";
import type { Provider } from "@src/interfaces/provider.interface";
import type { ProviderPhotoKey } from "@src/interfaces/provider-photo.interface";
import { getLocalCenterProviderList } from "@src/services/local-center-provider-search.service";
import {
  getProviderPhotos,
  mapProviderPhotosByProviderKey,
} from "@src/services/provider-photos.service";
import { mapProviderList } from "@src/utils/mapping-utils";
import { buildProviderPhotoKey } from "@src/utils/provider-photo-utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface UseMeetTheTeamProvidersResult {
  providers: Provider[];
  isLoading: boolean;
  isFromApi: boolean;
  isPhotoLoading: (provider: Provider) => boolean;
  loadProviderPhoto: (provider: Provider) => void;
}

interface UseMeetTheTeamProvidersOptions {
  isUserUuidReady?: boolean;
}

const PHOTO_BATCH_SIZE = 10;

async function fetchProviderPhotosBatched(
  photoKeys: ProviderPhotoKey[],
  userUuid: string,
): Promise<Record<string, ReturnType<typeof mapProviderPhotosByProviderKey>[string]>> {
  const batches: ProviderPhotoKey[][] = [];
  for (let index = 0; index < photoKeys.length; index += PHOTO_BATCH_SIZE) {
    batches.push(photoKeys.slice(index, index + PHOTO_BATCH_SIZE));
  }

  const results = await Promise.all(
    batches.map((providers) => getProviderPhotos({ providers, user_uuid: userUuid })),
  );

  return results.reduce<ReturnType<typeof mapProviderPhotosByProviderKey>>((photos, response) => {
    if (response.success && response.data?.photos) {
      Object.assign(photos, mapProviderPhotosByProviderKey(response.data.photos));
    }
    return photos;
  }, {});
}

const getProviderPhotoKey = (provider: Provider): ProviderPhotoKey | null => {
  const contextId = Number(provider.contextId);
  if (!Number.isInteger(contextId) || contextId <= 0) {
    return null;
  }

  return {
    contextId,
    providerId: String(provider.providerId),
  };
};

export const sortProvidersPhotoFirst = (providers: Provider[]): Provider[] => {
  return providers
    .map((provider, index) => ({ provider, index }))
    .sort((left, right) => {
      const leftHasPhoto = left.provider.providerPhoto?.hasPhoto === true ? 1 : 0;
      const rightHasPhoto = right.provider.providerPhoto?.hasPhoto === true ? 1 : 0;

      if (leftHasPhoto !== rightHasPhoto) {
        return rightHasPhoto - leftHasPhoto;
      }

      return left.index - right.index;
    })
    .map(({ provider }) => provider);
};

export function useMeetTheTeamProviders({
  isUserUuidReady = true,
}: UseMeetTheTeamProvidersOptions = {}): UseMeetTheTeamProvidersResult {
  const { activeThemeConfig } = useThemeContext();
  const { data } = useApplicationContext();
  const { state, providerGroupId, departmentIds } = activeThemeConfig ?? {};
  const groupIds = useMemo<number[]>(() => {
    if (Array.isArray(providerGroupId)) {
      return providerGroupId;
    }

    return typeof providerGroupId === "number" ? [providerGroupId] : [];
  }, [providerGroupId]);
  const localCenterState = state?.trim();
  const hasCompleteLocalCenterScope =
    Boolean(localCenterState) &&
    groupIds.length > 0 &&
    groupIds.every((groupId) => Number.isInteger(groupId) && groupId > 0);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFromApi, setIsFromApi] = useState(false);
  const [loadingPhotoKeys, setLoadingPhotoKeys] = useState<Set<string>>(new Set());
  const photoQueueRef = useRef<ProviderPhotoKey[]>([]);
  const queuedPhotoKeysRef = useRef(new Set<string>());
  const loadablePhotoKeysRef = useRef(new Set<string>());
  const settledPhotoKeysRef = useRef(new Set<string>());
  const isPhotoFetchInProgressRef = useRef(false);
  const requestVersionRef = useRef(0);
  const userUuidRef = useRef(data.userUuid);
  userUuidRef.current = data.userUuid;

  const flushPhotoQueue = useCallback(async (requestVersion: number, userUuid: string) => {
    if (isPhotoFetchInProgressRef.current) {
      return;
    }

    const batch = photoQueueRef.current.splice(0, 10);
    if (batch.length === 0) {
      return;
    }

    isPhotoFetchInProgressRef.current = true;
    try {
      const photoMap = await fetchProviderPhotosBatched(batch, userUuid);
      if (requestVersion === requestVersionRef.current) {
        setProviders((currentProviders) =>
          currentProviders.map((provider) => {
            const photoKey = getProviderPhotoKey(provider);
            const photo = photoKey ? photoMap[buildProviderPhotoKey(photoKey)] : undefined;
            return photo ? { ...provider, providerPhoto: photo } : provider;
          }),
        );
      }
    } finally {
      if (requestVersion === requestVersionRef.current) {
        batch.forEach((photoKey) => {
          const key = buildProviderPhotoKey(photoKey);
          queuedPhotoKeysRef.current.delete(key);
          settledPhotoKeysRef.current.add(key);
        });
        setLoadingPhotoKeys((currentKeys) => {
          const remainingKeys = new Set(currentKeys);
          batch.forEach((photoKey) => {
            remainingKeys.delete(buildProviderPhotoKey(photoKey));
          });
          return remainingKeys;
        });
      }
      isPhotoFetchInProgressRef.current = false;

      const nextUserUuid = userUuidRef.current;
      if (photoQueueRef.current.length > 0 && nextUserUuid) {
        await flushPhotoQueue(requestVersionRef.current, nextUserUuid);
      } else if (
        requestVersion === requestVersionRef.current &&
        settledPhotoKeysRef.current.size === loadablePhotoKeysRef.current.size
      ) {
        setProviders((currentProviders) => sortProvidersPhotoFirst(currentProviders));
      }
    }
  }, []);

  const loadProviderPhoto = useCallback(
    (provider: Provider) => {
      const photoKey = getProviderPhotoKey(provider);
      const userUuid = data.userUuid;
      if (
        !photoKey ||
        !userUuid ||
        provider.providerPhoto ||
        queuedPhotoKeysRef.current.has(buildProviderPhotoKey(photoKey))
      ) {
        return;
      }

      const providerPhotoKey = buildProviderPhotoKey(photoKey);
      queuedPhotoKeysRef.current.add(providerPhotoKey);
      photoQueueRef.current.push(photoKey);
      setLoadingPhotoKeys((currentKeys) => new Set(currentKeys).add(providerPhotoKey));
      window.setTimeout(() => {
        flushPhotoQueue(requestVersionRef.current, userUuid);
      }, 0);
    },
    [data.userUuid, flushPhotoQueue],
  );

  const isPhotoLoading = useCallback(
    (provider: Provider) => {
      const photoKey = getProviderPhotoKey(provider);
      return photoKey ? loadingPhotoKeys.has(buildProviderPhotoKey(photoKey)) : false;
    },
    [loadingPhotoKeys],
  );

  useEffect(() => {
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    photoQueueRef.current = [];
    queuedPhotoKeysRef.current.clear();
    loadablePhotoKeysRef.current.clear();
    settledPhotoKeysRef.current.clear();
    setLoadingPhotoKeys(new Set());

    if (!hasCompleteLocalCenterScope || !localCenterState) {
      setProviders([]);
      setIsFromApi(false);
      setIsLoading(false);
      return;
    }

    const userUuid = data.userUuid;
    if (!isUserUuidReady || !userUuid) {
      setIsLoading(true);
      return;
    }

    setIsLoading(true);
    const loadProviders = async (): Promise<void> => {
      try {
        const response = await getLocalCenterProviderList({
          state: localCenterState,
          providerGroupId: groupIds,
          userUuid,
          ...(departmentIds ? { departmentIds } : {}),
        });

        if (!("message" in response) || !Array.isArray(response.message)) {
          setProviders([]);
          setIsFromApi(false);
          return;
        }

        const providerIds = new Set<string>();
        const mappedProviders = mapProviderList(response.message).filter((provider) => {
          const providerId = String(provider.providerId);
          if (providerIds.has(providerId)) {
            return false;
          }
          providerIds.add(providerId);
          return true;
        });

        if (requestVersion === requestVersionRef.current) {
          loadablePhotoKeysRef.current = new Set(
            mappedProviders.flatMap((provider) => {
              const photoKey = getProviderPhotoKey(provider);
              return photoKey && !provider.providerPhoto ? [buildProviderPhotoKey(photoKey)] : [];
            }),
          );
          setProviders(mappedProviders);
          setIsFromApi(mappedProviders.length > 0);
        }
      } catch {
        setProviders([]);
        setIsFromApi(false);
      } finally {
        if (requestVersion === requestVersionRef.current) {
          setIsLoading(false);
        }
      }
    };

    loadProviders();
  }, [
    data.userUuid,
    departmentIds,
    groupIds,
    hasCompleteLocalCenterScope,
    isUserUuidReady,
    localCenterState,
  ]);

  return { providers, isLoading, isFromApi, isPhotoLoading, loadProviderPhoto };
}
